<template>
  <div v-if="company" class="container-fluid pb-6">
    <EacDashboardHeaderComponent
      :company
      :isAdmin
      :activeSection="activePrimarySection"
      :activeSettingsTab="resolvedSettingsTab"
      :inventoryLabel="
        $t('dashboard.corporate.sections.energy_attribute_certificates')
      "
      :availableGos="portfolioItemsStore.totalAvailableGOs"
      :pendingPayout
      :isLoading
      @select:section="setPrimarySection"
      @open:settings-tab="openSettingsTab"
    />

    <div class="card mb-4">
      <div class="card-body">
        <TabsContainer
          v-if="company.id && activePrimarySection === 'eacs'"
          :tabs="eacTabs"
          @change:tab="handleEacTabChange"
          :activeTab="resolvedEacTab"
        >
          <template #tab-buy-and-cancel>
            <BulkBuyTableComponent :company-id="company.id!" />
          </template>
          <template #tab-overview>
            <FilterablePortfolioChartPanel />
          </template>
          <template #tab-receive>
            <ReceiveGoComponent :company="company" />
          </template>
          <template #tab-send>
            <SendGoComponent :company-id="company.id!" />
          </template>
          <template #tab-portfolios>
            <PortfoliosListComponent />
          </template>
          <template #tab-cancel>
            <CancelGoComponent :company-id="company.id!" :company="company" />
          </template>
          <template #tab-sell>
            <SellGoComponent :company="company" />
          </template>
          <template #tab-cancellation-documents>
            <CancellationStatementsComponent
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
          <template #tab-company-orders>
            <CompanyOrdersComponent
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
        </TabsContainer>

        <TabsContainer
          v-else-if="company.id && activePrimarySection === 'settings'"
          :tabs="settingsTabs"
          @change:tab="handleSettingsTabChange"
          :activeTab="resolvedSettingsTab"
        >
          <template #tab-settings>
            <SettingsComponent
              v-if="company.id"
              :companyId="company.id!"
              :company="company"
            />
          </template>
          <template #tab-submit-invoice>
            <RequestInvoiceComponent
              :company="company"
              :pendingInvoiceItems="pendingInvoiceItems"
              :defaultInvoiceNumber="defaultInvoiceNumber"
              @invoice-requested="handleInvoiceRequested"
            />
          </template>
          <template #tab-invoice-history>
            <InvoiceHistoryComponent
              v-if="company.id"
              :company_id="company.id!"
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
          <template #tab-documents>
            <UnifiedDocumentsTable
              :companies="[company]"
              :isAdmin="isAdmin || false"
              :showCompanyColumn="false"
            />
          </template>
        </TabsContainer>
      </div>
    </div>

    <MembershipAgreementModal v-if="mustAcceptTerms" :company="company" />
  </div>
  <loading-overlay-component v-if="isLoading"></loading-overlay-component>
</template>

<script setup lang="ts">
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useTabs, type Tab } from "@/composables/useTabs";
import MembershipAgreementModal from "@/dashboard/buyer/MembershipAgreementModal.vue";
import EacDashboardHeaderComponent, {
  type EacDashboardSection,
} from "@/dashboard/common/EacDashboardHeaderComponent.vue";
import ReceiveGoComponent from "@/dashboard/inventory_management/accounts/ReceiveGoComponent.vue";
import BulkBuyTableComponent from "@/dashboard/inventory_management/bulk_buy/BulkBuyTableComponent.vue";
import CancelGoComponent from "@/dashboard/inventory_management/cancel/CancelGoComponent.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import CompanyOrdersComponent from "@/dashboard/inventory_management/company_orders/CompanyOrdersComponent.vue";
import FilterablePortfolioChartPanel from "@/dashboard/inventory_management/overview/FilterablePortfolioChartPanel.vue";
import PortfoliosListComponent from "@/dashboard/inventory_management/portfolios/PortfoliosListComponent.vue";
import SellGoComponent from "@/dashboard/inventory_management/sell/SellGoComponent.vue";
import SendGoComponent from "@/dashboard/inventory_management/send/SendGoComponent.vue";
import CancellationStatementsComponent from "@/dashboard/invoice_management/CancellationStatementsComponent.vue";
import UnifiedDocumentsTable from "@/dashboard/invoice_management/documents/UnifiedDocumentsTable.vue";
import SettingsComponent from "@/dashboard/settings_management/SettingsComponent.vue";
import InvoiceHistoryComponent from "@/money/invoice/InvoiceHistoryComponent.vue";
import RequestInvoiceComponent from "@/money/invoice/RequestInvoiceComponent.vue";
import { usePendingInvoiceItems } from "@/money/invoice/usePendingInvoiceItems";
import type { Company, FeatureFlags } from "@/types";
import { computed, inject, onMounted, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company: Company;
  isAdmin: boolean;
}>();

const { t } = useI18n();
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;
const handleSuccess = inject("globalSuccessHandler") as (
  message: string,
  title: string
) => void;

const isLoading = ref(false);

const portfolioItemsStore = usePortfolioItemsStore();
const {
  pendingInvoiceItems,
  defaultInvoiceNumber,
  pendingPayout,
  canSubmitInvoice,
  fetchPendingInvoiceItems,
  clearPendingInvoiceItems,
} = usePendingInvoiceItems();

const featureFlags = computed<FeatureFlags>(
  () => props.company.feature_flags ?? {}
);
const hasCorporateVirtualAccounts = computed(
  () => !!featureFlags.value.corporate_virtual_accounts_enabled
);

const eacTabs = computed(() => {
  const tabs: Array<Tab & { enabled?: boolean }> = [
    {
      id: "buy-and-cancel",
      name: t("common.actions.buy_and_cancel"),
      icon: "shopping-cart",
      enabled: true,
    },
    {
      id: "overview",
      name: t("tradingCenter.tabs.overview"),
      icon: "chart-pie",
      enabled: true,
    },
    {
      id: "receive",
      name: t("common.actions.receive"),
      icon: "inbox",
      enabled: hasCorporateVirtualAccounts.value,
    },
    {
      id: "send",
      name: t("common.actions.send"),
      icon: "paper-plane",
      enabled: hasCorporateVirtualAccounts.value,
    },
    {
      id: "portfolios",
      name: t("common.actions.portfolios"),
      icon: "layer-group",
      enabled: hasCorporateVirtualAccounts.value,
    },
    {
      id: "cancel",
      name: t("common.actions.cancel"),
      icon: "ban",
      enabled: hasCorporateVirtualAccounts.value,
    },
    {
      id: "sell",
      name: t("common.actions.sell"),
      icon: "dollar-sign",
      enabled: hasCorporateVirtualAccounts.value,
    },
    {
      id: "cancellation-documents",
      name: t("dashboard.companyData.cancellation_documents"),
      icon: "file-circle-check",
      enabled: true,
    },
    {
      id: "company-orders",
      name: t("company_orders.title"),
      icon: "list-ul",
      enabled: true,
    },
  ];

  return tabs.filter((tab) => tab.enabled !== false);
});

const defaultEacTabId = computed(() => "buy-and-cancel");

const { activeTab: rawEacTab, setTab: setEacTab } = useTabs("corporate-eacs", {
  tabs: eacTabs,
  defaultTabId: () => defaultEacTabId.value,
});

const resolvedEacTab = computed(() => rawEacTab.value || defaultEacTabId.value);

const handleEacTabChange = (tabId: string) => {
  setEacTab(tabId);
};

const settingsTabs = computed<Tab[]>(() => [
  {
    id: "settings",
    name: t("settings.title"),
    icon: "gear",
  },
  ...(canSubmitInvoice.value
    ? [
        {
          id: "submit-invoice",
          name: t("dashboard.companyData.submit_invoice"),
          icon: "file-invoice-dollar",
        },
      ]
    : []),
  {
    id: "invoice-history",
    name: t("dashboard.companyData.invoice_history"),
    icon: "file-invoice-dollar",
  },
  {
    id: "documents",
    name: t("common.documents"),
    icon: "file-alt",
  },
]);

const { activeTab: rawSettingsTab, setTab: setSettingsTab } = useTabs(
  "corporate-settings",
  {
    tabs: settingsTabs,
    defaultTabId: "settings",
  }
);

const resolvedSettingsTab = computed(() => rawSettingsTab.value || "settings");

const handleSettingsTabChange = (tabId: string) => {
  setSettingsTab(tabId);
};

const activePrimarySection = ref<EacDashboardSection>("eacs");

const setPrimarySection = (sectionId: EacDashboardSection) => {
  activePrimarySection.value = sectionId;
};

const mustAcceptTerms = computed(() => {
  const hasSigned =
    props.company.membership_agreement_signed_at ||
    props.company.has_registered;
  return !hasSigned && !props.isAdmin;
});

const handleInvoiceRequested = () => {
  handleSuccess(
    t("dashboard.companyData.invoice_submission_success"),
    t("dashboard.companyData.invoice_paid_within_7_days")
  );
  openSettingsTab("invoice-history");
  clearPendingInvoiceItems();
};

const openSettingsTab = (tabId: string) => {
  setPrimarySection("settings");
  setSettingsTab(tabId);
};

async function loadCompanyData() {
  if (!props.company.id) return;

  isLoading.value = true;
  try {
    portfolioItemsStore.setAllItems([]);
    await Promise.all([
      portfolioItemsStore.fetchItems(props.company.id),
      fetchPendingInvoiceItems(props.company.id).catch(globalErrorHandler),
    ]);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}

provide(
  "navigateToInventoryTab",
  (action: "send" | "cancel" | "sell" | "receive" | "portfolios") => {
    setPrimarySection("eacs");
    if (
      ["send", "cancel", "receive", "portfolios"].includes(action) &&
      !hasCorporateVirtualAccounts.value
    ) {
      return;
    }

    const actionToTab: Record<
      "send" | "cancel" | "sell" | "receive" | "portfolios",
      string
    > = {
      send: "send",
      cancel: "cancel",
      sell: "sell",
      receive: "receive",
      portfolios: "portfolios",
    };
    handleEacTabChange(actionToTab[action]);
  }
);

watch(
  () => props.company.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      loadCompanyData();
    }
  }
);

onMounted(() => {
  loadCompanyData();
});
</script>

<style scoped lang="scss">
.container-fluid {
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
